/**
 * Dossier Cession Investisseurs — Groupe Rancelli
 * Mode bidirectionnel avec Valentine (push-to-talk + chat texte)
 * Scan multi-Gmail des bilans → analyse IA → valorisation → PDF premium versioné
 */
import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Briefcase, Mail, Mic, MicOff, Send, Loader2, Download, FileText,
  TrendingUp, Scale, Sparkles, RefreshCw, FileSearch, Volume2, Eye,
  Building2, Target, Zap, Save
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CHAMPS_FICHE = [
  { key: 'ha_plantes', label: 'Surface plantée (ha)', placeholder: '38 ha pommiers + 12 ha lavandin' },
  { key: 'varietes', label: 'Variétés / productions', placeholder: 'Golden, Granny / Lavandin Grosso / Thym' },
  { key: 'production_estimee', label: 'Production actuelle & projetée', placeholder: '850 t/an → 1200 t à 3 ans' },
  { key: 'projets', label: 'Projets (irrigation, matériel, bâtiment)', placeholder: 'Goutte-à-goutte 20 ha + hangar 800m² (2026)' },
  { key: 'aides', label: 'Aides / subventions / crédits d\'impôt', placeholder: 'PCAE 40%, bio 450€/ha, CI remplacement' },
  { key: 'strategie', label: 'Stratégie (positionnement, canaux)', placeholder: 'Marque propre + export Allemagne' },
  { key: 'cible_ca', label: 'Cible CA (€)', placeholder: '2 500 000 €' },
  { key: 'horizon', label: 'Horizon', placeholder: '5 ans' },
];

export default function DossierCessionPage() {
  const [scope, setScope] = useState(null);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanReport, setScanReport] = useState(null);
  const [analyzingBatch, setAnalyzingBatch] = useState(false);
  const [pieces, setPieces] = useState([]);
  const [selectedPiece, setSelectedPiece] = useState(null);

  // Chat
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [autoTTS, setAutoTTS] = useState(true);
  const [continuousMode, setContinuousMode] = useState(true);
  const [societeFocus, setSocieteFocus] = useState('');
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;

  // Valorisation
  const [scenario, setScenario] = useState('avec_eta');
  const [pctCession, setPctCession] = useState(20);
  const [valorisation, setValorisation] = useState(null);
  const [generatingDoc, setGeneratingDoc] = useState(false);

  // Fiche société
  const [showFicheDialog, setShowFicheDialog] = useState(false);
  const [ficheSocieteId, setFicheSocieteId] = useState(null);
  const [ficheData, setFicheData] = useState({});

  const loadAll = async () => {
    try {
      const [sc, th, pc] = await Promise.all([
        api.get('/dossier-cession/scope'),
        api.get('/dossier-cession/thread'),
        api.get('/dossier-cession/pieces'),
      ]);
      setScope(sc.data);
      setThread(th.data);
      setPieces(pc.data.pieces || []);
    } catch (e) {
      toast.error('Chargement dossier impossible');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [thread?.messages?.length]);

  const speak = (text) => {
    if (!synth || !autoTTS) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR';
    u.rate = 1.05;
    const voices = synth.getVoices();
    const fr = voices.find(v => v.lang.startsWith('fr'));
    if (fr) u.voice = fr;
    synth.speak(u);
  };

  const scanGmail = async () => {
    if (!window.confirm('Lancer le scan de toutes les boîtes Gmail connectées pour chercher les bilans et pièces comptables ?\n\nCela peut prendre quelques minutes.')) return;
    setScanning(true);
    setScanReport(null);
    try {
      const r = await api.post('/dossier-cession/scan-gmail', { max_par_societe: 200 });
      setScanReport(r.data);
      toast.success(`${r.data.total_pieces} pièces trouvées sur ${r.data.societes_scannees.length} boîtes`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan Gmail échoué');
    } finally {
      setScanning(false);
    }
  };

  const analyserBatch = async () => {
    setAnalyzingBatch(true);
    try {
      const r = await api.post('/dossier-cession/analyser-batch');
      toast.success(`${r.data.analysees} pièces analysées`);
      await loadAll();
    } catch (e) {
      toast.error('Analyse en lot échouée');
    } finally {
      setAnalyzingBatch(false);
    }
  };

  const analyserPiece = async (pid) => {
    toast.info('Analyse en cours…');
    try {
      const r = await api.post('/dossier-cession/analyser-piece', { piece_id: pid });
      if (r.data.warning) {
        toast.warning(r.data.warning);
      } else {
        toast.success('Pièce analysée');
      }
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analyse échouée');
    }
  };

  const send = async (overrideText = null) => {
    const text = overrideText || input;
    if (!text || !text.trim()) return;
    setSending(true);
    try {
      const r = await api.post('/dossier-cession/chat', {
        message: text,
        societe_focus: societeFocus || null,
      });
      setInput('');
      await loadAll();
      if (r.data.reply) speak(r.data.reply);
    } catch (e) {
      toast.error('Valentine n\'a pas répondu');
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      if (synth) synth.cancel();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream);
      const chunks = [];
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        if (blob.size < 500) { setRecording(false); recorderRef.current = null; return; }
        const fd = new FormData();
        fd.append('file', blob, 'input.webm');
        try {
          const t = await api.post('/journee-chauffeur/transcrire', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const text = t.data.text;
          if (text) {
            if (continuousMode) {
              setInput('');
              await send(text);
            } else {
              setInput(prev => prev ? `${prev} ${text}` : text);
            }
          }
        } catch { toast.error('Transcription impossible'); }
      };
      r.start();
      recorderRef.current = r;
      setRecording(true);
    } catch {
      toast.error('Micro refusé');
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
      setRecording(false);
      recorderRef.current = null;
    }
  };

  const calculer = async () => {
    try {
      const r = await api.post('/dossier-cession/calculer-valorisation', {
        scenario,
        pourcentage_cession: parseFloat(pctCession) || 20,
      });
      setValorisation(r.data);
      toast.success('Valorisation calculée');
    } catch (e) {
      toast.error('Calcul impossible');
    }
  };

  const genererDossier = async () => {
    setGeneratingDoc(true);
    try {
      const r = await api.post('/dossier-cession/generer-dossier', { scenario });
      toast.success('Dossier généré !');
      const url = r.data.download_url;
      if (url) {
        const a = document.createElement('a');
        a.href = `${process.env.REACT_APP_BACKEND_URL}${url}`;
        a.download = r.data.doc.filename;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Génération échouée');
    } finally {
      setGeneratingDoc(false);
    }
  };

  const openFiche = (societe) => {
    setFicheSocieteId(societe.societe_id);
    setFicheData(societe.synthese?.fiche || {});
    setShowFicheDialog(true);
  };

  const saveFiche = async () => {
    try {
      await api.post('/dossier-cession/fiche-societe', {
        societe_id: ficheSocieteId,
        fiche: ficheData,
      });
      toast.success('Fiche enregistrée');
      setShowFicheDialog(false);
      await loadAll();
    } catch { toast.error('Enregistrement impossible'); }
  };

  const fmtEur = (n) => {
    if (n == null) return 'n.c.';
    try { return `${Math.round(n).toLocaleString('fr-FR')} €`; } catch { return `${n} €`; }
  };

  if (loading) return <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>;

  const societesCession = scope?.societes?.filter(s => s.role === 'cession') || [];
  const societesShowcase = scope?.societes?.filter(s => s.role === 'showcase') || [];
  const nbPiecesAnalysees = pieces.filter(p => p.analyse).length;

  return (
    <div className="p-4 space-y-4" data-testid="dossier-cession-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Briefcase className="w-7 h-7 text-emerald-700" />
            Dossier Cession Investisseurs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Scanne les Gmail → analyse les bilans → co-construit avec Valentine → génère un PDF premium pour les investisseurs.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={scanGmail} disabled={scanning} data-testid="btn-scan-gmail" className="bg-emerald-600 hover:bg-emerald-700">
            {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Scanner tous les Gmail
          </Button>
          <Button onClick={analyserBatch} disabled={analyzingBatch || pieces.length === 0} variant="outline" data-testid="btn-analyser-batch">
            {analyzingBatch ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSearch className="w-4 h-4 mr-2" />}
            Analyser les bilans (IA)
          </Button>
          <Button onClick={loadAll} variant="ghost" size="sm" data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Rapport de scan */}
      {scanReport && (
        <Card className="border-emerald-500 border-2" data-testid="scan-report">
          <CardContent className="p-3 text-sm">
            <div className="font-semibold mb-1">📊 Rapport de scan — {scanReport.total_pieces} pièces trouvées</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              {scanReport.societes_scannees.map(s => (
                <div key={s.societe_id} className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                  <strong>{s.societe_nom}</strong> — {s.pieces_trouvees} pièces
                </div>
              ))}
            </div>
            {scanReport.erreurs?.length > 0 && (
              <div className="mt-2 text-xs text-amber-700">⚠ {scanReport.erreurs.length} erreur(s) : {scanReport.erreurs.join(' · ')}</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats haut */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card data-testid="stat-societes-cession"><CardContent className="p-3"><div className="text-xs text-muted-foreground">Sociétés cédées</div><div className="text-2xl font-bold text-emerald-700">{societesCession.length}</div></CardContent></Card>
        <Card data-testid="stat-pieces-total"><CardContent className="p-3"><div className="text-xs text-muted-foreground">Pièces scannées</div><div className="text-2xl font-bold text-blue-700">{pieces.length}</div></CardContent></Card>
        <Card data-testid="stat-pieces-analysees"><CardContent className="p-3"><div className="text-xs text-muted-foreground">Analysées IA</div><div className="text-2xl font-bold text-purple-700">{nbPiecesAnalysees}</div></CardContent></Card>
        <Card data-testid="stat-versions"><CardContent className="p-3"><div className="text-xs text-muted-foreground">Versions PDF</div><div className="text-2xl font-bold text-amber-700">{scope?.nb_versions_pdf || 0}</div></CardContent></Card>
      </div>

      <Tabs defaultValue="societes" className="w-full">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="societes" data-testid="tab-societes">Sociétés</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat">Chat Valentine</TabsTrigger>
          <TabsTrigger value="pieces" data-testid="tab-pieces">Pièces ({pieces.length})</TabsTrigger>
          <TabsTrigger value="valorisation" data-testid="tab-valorisation">Valorisation & PDF</TabsTrigger>
        </TabsList>

        {/* ========== SOCIÉTÉS ========== */}
        <TabsContent value="societes" className="space-y-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-emerald-700" /> Périmètre de cession</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {societesCession.length === 0 && <p className="text-sm text-muted-foreground italic">Aucune société identifiée. Les sociétés nommées « Vergers Bio », « ETA Sauvayre », « Distillerie Rancelli » ou « CASIA » seront détectées automatiquement.</p>}
              {societesCession.map(s => (
                <div key={s.societe_id} className="flex items-center justify-between p-3 border rounded hover:bg-muted/50" data-testid={`soc-cession-${s.societe_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{s.nom}</div>
                    <div className="text-xs text-muted-foreground">SIRET {s.siret || 'n.c.'} · {s.nb_pieces_scannees} pièces · {s.synthese?.fiche ? '✓ Fiche OK' : '○ Fiche à remplir'}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openFiche(s)} data-testid={`btn-fiche-${s.societe_id}`}><Target className="w-3 h-3 mr-1" />Fiche</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setSocieteFocus(s.nom); toast.info(`Chat recentré sur ${s.nom}`); }} data-testid={`btn-focus-${s.societe_id}`}><Zap className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {societesShowcase.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Vitrine (hors cession)</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {societesShowcase.map(s => <Badge key={s.societe_id} variant="outline">{s.nom}</Badge>)}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== CHAT VALENTINE ========== */}
        <TabsContent value="chat">
          <Card className="h-[70vh] flex flex-col">
            <CardHeader className="pb-2 border-b flex-row items-center justify-between gap-2 space-y-0">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-600" />
                <CardTitle className="text-base">Mode bidirectionnel avec Valentine</CardTitle>
                {societeFocus && <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300" data-testid="chip-focus">🎯 {societeFocus}</Badge>}
              </div>
              <div className="flex gap-2 items-center text-xs">
                <select className="border rounded px-1 py-0.5 bg-background" value={societeFocus} onChange={(e) => setSocieteFocus(e.target.value)} data-testid="select-focus">
                  <option value="">Tout le dossier</option>
                  {societesCession.map(s => <option key={s.societe_id} value={s.nom}>{s.nom}</option>)}
                </select>
                <label className="flex items-center gap-1 cursor-pointer" title="Mode tracteur : auto-envoi + lecture auto"><input type="checkbox" checked={continuousMode} onChange={(e) => setContinuousMode(e.target.checked)} className="w-3.5 h-3.5" data-testid="toggle-continuous" />🚜</label>
                <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={autoTTS} onChange={(e) => setAutoTTS(e.target.checked)} className="w-3.5 h-3.5" data-testid="toggle-tts" />🔊</label>
              </div>
            </CardHeader>
            <CardContent ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-messages">
              {(!thread?.messages || thread.messages.length === 0) && (
                <div className="text-center text-muted-foreground text-sm py-8 space-y-2">
                  <p>👋 Démarre en lançant <strong>Scanner tous les Gmail</strong> ou parle direct à Valentine.</p>
                  <p className="text-xs">Ex : « Attaque le dossier. Commence par Vergers Bio. »</p>
                </div>
              )}
              {thread?.messages?.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-3 rounded-lg text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-emerald-600 text-white' : 'bg-muted'}`}>
                    {m.content}
                    {m.role === 'assistant' && (
                      <button onClick={() => speak(m.content)} className="ml-2 opacity-60 hover:opacity-100" title="Lire"><Volume2 className="w-3 h-3 inline" /></button>
                    )}
                  </div>
                </div>
              ))}
              {sending && <div className="flex justify-start"><div className="bg-muted p-3 rounded-lg text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Valentine réfléchit…</div></div>}
            </CardContent>
            <div className="p-3 border-t flex gap-2 items-end">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={continuousMode ? "🚜 Push-to-talk : maintiens le micro et parle" : "Dis à Valentine ce qu'on travaille…"}
                className="min-h-[48px] max-h-32 resize-none"
                rows={2}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                data-testid="chat-input"
              />
              <Button
                size="lg"
                variant={recording ? 'destructive' : 'default'}
                className={`${recording ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-emerald-600 hover:bg-emerald-700'} h-16 w-16 rounded-full shrink-0`}
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                onMouseLeave={() => { if (recording) stopRecording(); }}
                onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
                onTouchEnd={(e) => { e.preventDefault(); stopRecording(); }}
                onContextMenu={(e) => e.preventDefault()}
                style={{ touchAction: 'none', userSelect: 'none' }}
                data-testid="btn-mic"
                title="Maintenir pour parler"
              >
                {recording ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </Button>
              <Button onClick={() => send()} disabled={sending || !input.trim()} size="icon" className="h-16 w-12 shrink-0" data-testid="btn-send">
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* ========== PIÈCES ========== */}
        <TabsContent value="pieces">
          <Card>
            <CardContent className="p-3">
              {pieces.length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-6 text-center">Aucune pièce. Lance le scan Gmail.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left border-b">
                      <tr className="text-muted-foreground">
                        <th className="p-2">Société</th>
                        <th className="p-2">Type</th>
                        <th className="p-2">Année</th>
                        <th className="p-2">Fichier</th>
                        <th className="p-2 text-right">CA / EBE</th>
                        <th className="p-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pieces.map(p => (
                        <tr key={p.id} className="border-b hover:bg-muted/40" data-testid={`piece-${p.id}`}>
                          <td className="p-2 font-medium">{p.societe_nom}</td>
                          <td className="p-2"><Badge variant="outline">{p.type_piece}</Badge></td>
                          <td className="p-2">{p.annee_exercice || '—'}</td>
                          <td className="p-2 truncate max-w-[200px]" title={p.filename}>{p.filename}</td>
                          <td className="p-2 text-right font-mono">
                            {p.analyse ? `${fmtEur(p.analyse.ca_ht)} / ${fmtEur(p.analyse.ebe)}` : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="p-2 text-right">
                            <Button size="sm" variant="ghost" onClick={() => analyserPiece(p.id)} data-testid={`btn-analyse-${p.id}`}>
                              {p.analyse ? <Eye className="w-3 h-3" /> : <FileSearch className="w-3 h-3" />}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== VALORISATION ========== */}
        <TabsContent value="valorisation" className="space-y-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2"><Scale className="w-5 h-5" /> Scénario & Hypothèses</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-xs">Scénario</Label>
                  <div className="flex gap-2 mt-1">
                    <Button size="sm" variant={scenario === 'avec_eta' ? 'default' : 'outline'} onClick={() => setScenario('avec_eta')} className={scenario === 'avec_eta' ? 'bg-emerald-600' : ''} data-testid="scen-avec-eta">AVEC ETA Sauvayre</Button>
                    <Button size="sm" variant={scenario === 'sans_eta' ? 'default' : 'outline'} onClick={() => setScenario('sans_eta')} className={scenario === 'sans_eta' ? 'bg-emerald-600' : ''} data-testid="scen-sans-eta">SANS ETA Sauvayre</Button>
                  </div>
                </div>
                <div className="w-32">
                  <Label className="text-xs">% cédés</Label>
                  <Input type="number" value={pctCession} onChange={(e) => setPctCession(e.target.value)} min={5} max={49} data-testid="input-pct" />
                </div>
                <Button onClick={calculer} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-calculer-valo">
                  <TrendingUp className="w-4 h-4 mr-2" /> Calculer
                </Button>
                <Button onClick={genererDossier} disabled={generatingDoc} className="bg-emerald-700 hover:bg-emerald-800 ml-auto" data-testid="btn-generer-pdf">
                  {generatingDoc ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Générer PDF V{(scope?.nb_versions_pdf || 0) + 1}
                </Button>
              </div>

              {valorisation && (
                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded space-y-2" data-testid="valo-result">
                  <div className="text-sm font-semibold">Résultat — {scenario === 'avec_eta' ? 'AVEC ETA' : 'SANS ETA'} ({valorisation.pourcentage_cession}%)</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    <div className="bg-white rounded p-2 border"><div className="text-muted-foreground">CA consolidé</div><div className="font-bold text-base">{fmtEur(valorisation.agrege.ca_total)}</div></div>
                    <div className="bg-white rounded p-2 border"><div className="text-muted-foreground">EBE</div><div className="font-bold text-base text-emerald-700">{fmtEur(valorisation.agrege.ebe_total)}</div></div>
                    <div className="bg-white rounded p-2 border"><div className="text-muted-foreground">Trésorerie</div><div className="font-bold text-base">{fmtEur(valorisation.agrege.tresorerie_total)}</div></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-emerald-100 rounded p-3 border border-emerald-300">
                      <div className="text-xs text-emerald-900">Valorisation (fourchette)</div>
                      <div className="font-bold text-lg">{fmtEur(valorisation.methodes.mid_min)} — {fmtEur(valorisation.methodes.mid_max)}</div>
                    </div>
                    <div className="bg-amber-100 rounded p-3 border border-amber-300">
                      <div className="text-xs text-amber-900">Montant pour {valorisation.pourcentage_cession}%</div>
                      <div className="font-bold text-lg">{fmtEur(valorisation.levee_pour_pct.min)} — {fmtEur(valorisation.levee_pour_pct.max)}</div>
                    </div>
                  </div>
                  <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                    {valorisation.notes.map((n, i) => <li key={i}>• {n}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {thread?.versions_pdf?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Versions générées</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {thread.versions_pdf.slice().reverse().map(v => (
                  <a
                    key={v.id}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/dossier-cession/download/${v.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between p-2 border rounded hover:bg-muted/50"
                    data-testid={`version-${v.id}`}
                  >
                    <div>
                      <div className="font-semibold text-sm">V{v.version} — {v.titre}</div>
                      <div className="text-xs text-muted-foreground">{v.scenario} · {(v.size_bytes / 1024).toFixed(0)} Ko · {(v.created_at || '').slice(0, 10)}</div>
                    </div>
                    <Download className="w-4 h-4 text-emerald-700" />
                  </a>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog fiche société */}
      <Dialog open={showFicheDialog} onOpenChange={setShowFicheDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Fiche société</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {CHAMPS_FICHE.map(c => (
              <div key={c.key}>
                <Label className="text-xs">{c.label}</Label>
                <Textarea
                  value={ficheData[c.key] || ''}
                  onChange={(e) => setFicheData({ ...ficheData, [c.key]: e.target.value })}
                  placeholder={c.placeholder}
                  rows={2}
                  data-testid={`fiche-${c.key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFicheDialog(false)}>Annuler</Button>
            <Button onClick={saveFiche} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-fiche"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
